import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Icon,
  IconButton,
  CircularProgress,
  Pagination,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import { useNavigate } from "react-router-dom";
import UserApi from "../../../../Classes/API/UsersApi";

const EvalPatientsModal = ({ open, onClose, AppDay }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const fetchEvalPatients = useCallback(
    async (currentPage) => {
      setLoading(true);
      try {
        console.log("Fetching eval patients for AppDay:", AppDay);
        const req = {
          OnlyWithEvalSessions: true,
          AppDay: AppDay?.dateDay,
          pagingParams: {
            PageNumber: currentPage,
            PageSize: rowsPerPage,
          },
        };

        const res = await UserApi.getPatients(req);

        if (res.status === "Success") {
          setPatients(res.data.list || []);
          const count = res.data.totalItems;
          setTotalPages(Math.ceil(count / rowsPerPage));
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
      } finally {
        setLoading(false);
      }
    },
    [rowsPerPage]
  );

  useEffect(() => {
    if (open) {
      fetchEvalPatients(page);
    } else {
      setPage(1);
    }
  }, [open, page, fetchEvalPatients]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const columns = [
    { Header: "Patient Name", accessor: "name", align: "left" },
    { Header: "sessionType", accessor: "sessionType", align: "left" },
    { Header: "Actions", accessor: "action", align: "center" },
  ];

  const rows = patients.map((patient) => ({
    name: (
      <MDTypography variant="button" fontWeight="medium">
        {patient?.getUser?.name || "N/A"}
      </MDTypography>
    ),
    sessionType: (
      <MDBox display="flex" flexDirection="column">
        <MDTypography variant="caption" fontWeight="bold">
          {patient?.sessionType}
        </MDTypography>
      </MDBox>
    ),
    action: (
      <MDButton
        variant="gradient"
        color="info"
        size="small"
        onClick={() => navigate(`/PatientHistory/${patient?.id}`)}
      >
        <Icon>history</Icon>&nbsp;History
      </MDButton>
    ),
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <MDBox
        sx={{
          position: "relative",
          width: { xs: "95%", md: "800px" },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <MDTypography variant="h5" fontWeight="medium">
            Evaluation Patients List
          </MDTypography>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>

        <MDBox sx={{ flexGrow: 1, overflowY: "auto", minHeight: "400px" }}>
          {loading ? (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              pt={10}
            >
              <CircularProgress color="info" />
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
            />
          )}
        </MDBox>

        <MDBox
          component="footer"
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: "1px solid #f0f2f5",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="info"
              variant="outlined"
              shape="rounded"
              disabled={loading}
              showFirstButton
              showLastButton
            />
          )}

          <MDBox width="100%" display="flex" justifyContent="flex-end">
            <MDButton variant="outlined" color="secondary" onClick={onClose}>
              Close
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
};

export default EvalPatientsModal;
